import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../lib/api'

export interface MyCertificate {
  id: string
  course_title: string
  certificate_code: string
  issued_at: string
  // Hoy /academy/certificates/my no devuelve estos campos; si la API los agrega, el empate pasa a ser por id.
  course_id?: string
  course_slug?: string
}

export type CertificateLookup =
  | { status: 'loading' | 'error' | 'none' | 'ambiguous' }
  | { status: 'found'; code: string }

interface CourseRef {
  id?: string
  slug?: string
  title: string
}

const normalize = (value: string) => value.trim().toLocaleLowerCase('es-CO')

/**
 * Empata un certificado con un curso. TODO: cuando la API devuelva course_id en
 * /certificates/my, quitar el empate por título (dos cursos con el mismo título
 * o un curso renombrado después de emitir dan resultados erróneos).
 */
export function matchCertificate(list: MyCertificate[], course: CourseRef): CertificateLookup {
  const byId = list.filter((c) => (course.id && c.course_id === course.id) || (course.slug && c.course_slug === course.slug))
  const candidates = byId.length > 0 ? byId : list.filter((c) => normalize(c.course_title) === normalize(course.title))
  const codes = new Set(candidates.map((c) => c.certificate_code))
  if (codes.size === 0) return { status: 'none' }
  if (codes.size > 1) return { status: 'ambiguous' }
  return { status: 'found', code: candidates[0].certificate_code }
}

interface UseMyCertificatesOptions {
  /** Si se pasa un curso, reconsulta cada 8 s (máx. 6 veces) mientras no haya certificado para él. */
  pollWhileNone?: CourseRef
}

const POLL_INTERVAL_MS = 8000
const POLL_MAX_TRIES = 6

/** Carga los certificados del estudiante (si `enabled`) y expone `find(curso)` y `refetch()`. */
export function useMyCertificates(enabled = true, options: UseMyCertificatesOptions = {}) {
  const [certificates, setCertificates] = useState<MyCertificate[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const pollCourse = options.pollWhileNone
  const [pollExhausted, setPollExhausted] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const cancelledRef = useRef(false)

  const load = useCallback(async (silent: boolean) => {
    if (!silent) setState('loading')
    try {
      const { data } = await api.get('/academy/certificates/my')
      if (cancelledRef.current) return null
      const list = (data.data ?? []) as MyCertificate[]
      setCertificates(list)
      setState('ready')
      return list
    } catch {
      if (!cancelledRef.current) setState('error')
      return null
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    cancelledRef.current = false
    load(false)
    return () => { cancelledRef.current = true }
  }, [enabled, load])

  const pollKey = pollCourse ? `${pollCourse.id ?? ''}|${pollCourse.slug ?? ''}|${pollCourse.title}` : ''
  const needsPoll = enabled && !!pollCourse && state === 'ready' && matchCertificate(certificates, pollCourse).status === 'none'

  useEffect(() => {
    if (!needsPoll || !pollCourse) return
    setPollExhausted(false)
    let tries = 0
    const timer = window.setInterval(async () => {
      tries += 1
      const list = await load(true)
      const stillNone = !list || matchCertificate(list, pollCourse).status === 'none'
      if (cancelledRef.current || tries >= POLL_MAX_TRIES || !stillNone) {
        window.clearInterval(timer)
        if (!cancelledRef.current && tries >= POLL_MAX_TRIES && stillNone) setPollExhausted(true)
      }
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
    // pollKey representa a pollCourse; needsPoll corta el sondeo al aparecer el certificado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsPoll, pollKey, load])

  const find = useCallback(
    (course: CourseRef): CertificateLookup => {
      if (state === 'loading') return { status: 'loading' }
      if (state === 'error') return { status: 'error' }
      return matchCertificate(certificates, course)
    },
    [certificates, state],
  )

  // Refresco silencioso: conserva el estado anterior (el aviso no se vacía) y solo marca `refreshing`.
  const refetch = useCallback(async () => {
    setRefreshing(true)
    try {
      return await load(true)
    } finally {
      if (!cancelledRef.current) setRefreshing(false)
    }
  }, [load])

  return { certificates, state, find, refetch, refreshing, pollExhausted }
}
